import { addDays, type Member } from '@hive/shared';

/** "Today", "Tomorrow", "Mon 26 Oct", or the year too when it isn't this one. */
export function formatDue(due: string, today: string): string {
  if (due === today) return 'Today';
  if (due === addDays(today, 1)) return 'Tomorrow';
  if (due === addDays(today, -1)) return 'Yesterday';
  const [y, m, d] = due.split('-').map(Number) as [number, number, number];
  const date = new Date(y, m - 1, d);
  const sameYear = due.slice(0, 4) === today.slice(0, 4);
  return date.toLocaleDateString('en-GB', {
    weekday: sameYear ? 'short' : undefined,
    day: 'numeric',
    month: 'short',
    year: sameYear ? undefined : 'numeric',
  });
}

export function dueTone(due: string, today: string, done: boolean): 'overdue' | 'soon' | 'later' {
  if (done) return 'later';
  if (due < today) return 'overdue';
  if (due <= addDays(today, 1)) return 'soon';
  return 'later';
}

/** "5m ago", "3h ago", "2d ago", then the date. */
export function timeAgo(iso: string, now = new Date()): string {
  const seconds = Math.max(0, (now.getTime() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function memberName(members: readonly Member[], id: string | null | undefined): string | null {
  if (!id) return null;
  return members.find((m) => m.id === id)?.display_name ?? 'Former member';
}

/** A timestamp as a datetime-local input value ("2026-10-24T14:05"), in local time. */
export function toLocalDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A datetime-local input value back to an ISO timestamp, or null if it isn't one. */
export function fromLocalDateTime(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
